import type { CorrelationRisk, TopEntity } from '../../types';
import { riskCovered } from './riskTerms';

/** What the Detected entities row offers for a group (FB-1007 #5). */
export type TopEntityState = 'redacted' | 'in_dictionary' | 'redact';

/**
 * Done when every occurrence is already redacted, or when a dictionary rule
 * covers it (its rescan redacts the rest); only otherwise is Redact offered.
 */
export function topEntityState(entity: TopEntity): TopEntityState {
  if (entity.count > 0 && (entity.redacted ?? 0) >= entity.count) return 'redacted';
  if (entity.in_dictionary) return 'in_dictionary';
  return 'redact';
}

/** The words a Redact adds to the case dictionary: every spelling of the group. */
export function wordsToRedact(entity: TopEntity): string[] {
  const words = entity.spellings?.length ? entity.spellings : [entity.text];
  return [...new Set(words.map((w) => w.trim()).filter(Boolean))];
}

/** "+1 spelling: Darwin AI Navigator" for a grouped row, or null. */
export function otherSpellings(entity: TopEntity): string | null {
  const others = (entity.spellings ?? []).filter((s) => s !== entity.text);
  if (others.length === 0) return null;
  return `+${others.length} ${others.length === 1 ? 'spelling' : 'spellings'}: ${others.join(', ')}`;
}

/** Every term the risk preselects (its redact terms; all terms on a risk stored
 *  before roles) is redacted by the dictionary — derived from the server's
 *  `in_dictionary`, so it survives reopening the dialog (FB-1007 #6); `pending` holds
 *  the terms this dialog just added until the next fetch confirms them. */
export function riskApplied(risk: CorrelationRisk, pending: Record<string, boolean> = {}): boolean {
  return risk.involved_terms.length > 0 && riskCovered(risk, pending);
}

/** The Context awareness badge: risks not yet handled (their terms aren't all in the
 *  dictionary, and nobody dismissed them — FR-14). */
export function openRiskCount(risks: CorrelationRisk[] | undefined): number {
  return (risks ?? []).filter((risk) => !risk.dismissed && !riskApplied(risk)).length;
}
